import React, {useEffect, useState} from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
} from "@mui/material";
import {
  getFeedingTrayChecks,
  createFeedingTrayCheck,
  updateFeedingTrayCheck,
  uploadFeedingTrayImages,
  removeFeedingTrayImage,
  getImageSignedUrl,
} from "../../api/pond";
import ImageUpload from "../../components/common/ImageUpload";
import FeedTrayAnalysis from "../../components/common/FeedTrayAnalysis";
import AddIcon from "@mui/icons-material/Add";
import {useTranslation} from "react-i18next";
import AnalyticsIcon from "@mui/icons-material/Analytics";

interface FeedingTrayTabProps {
  pondId: string;
  cycleId?: string;
  isCycleActive?: boolean;
}

interface ImageThumbnailsProps {
  imageUrls: string[];
  pondId: string;
}

// Replace the ImageThumbnails component with a secure, authenticated S3 image fetch implementation
const ImageThumbnails: React.FC<ImageThumbnailsProps> = ({imageUrls, pondId}) => {
  const [blobUrls, setBlobUrls] = useState<{[key: string]: string}>({});
  const [loading, setLoading] = useState<{[key: string]: boolean}>({});
  const [showGallery, setShowGallery] = useState(false);

  useEffect(() => {
    imageUrls.forEach((s3Key) => {
      if (!blobUrls[s3Key] && !s3Key.startsWith("blob:") && !s3Key.startsWith("http")) {
        setLoading((prev) => ({...prev, [s3Key]: true}));
        const token = localStorage.getItem("token");

        const baseUrl =
          process.env.NODE_ENV === "production" ? process.env.REACT_APP_API_URL || "https://api.smartfarm.com" : "http://localhost:3001";

        fetch(`${baseUrl}/api/ponds/images/${encodeURIComponent(s3Key)}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "image/*",
          },
        })
          .then((res) => {
            if (!res.ok) throw new Error("Failed to fetch image");
            return res.blob();
          })
          .then((blob) => {
            const reader = new FileReader();
            reader.onload = () => {
              const dataUrl = reader.result as string;
              setBlobUrls((prev) => ({...prev, [s3Key]: dataUrl}));
            };
            reader.readAsDataURL(blob);
          })
          .catch((error) => {
            console.error("Failed to fetch image for:", s3Key, error);
            setBlobUrls((prev) => ({...prev, [s3Key]: ""}));
          })
          .finally(() => {
            setLoading((prev) => ({...prev, [s3Key]: false}));
          });
      }
    });
    // eslint-disable-next-line
  }, [imageUrls]);

  const handleImageClick = async (s3Key: string) => {
    // Fetch and open full image in new tab
    const token = localStorage.getItem("token");
    const baseUrl = process.env.NODE_ENV === "production" ? process.env.REACT_APP_API_URL || "https://api.smartfarm.com" : "http://localhost:3001";

    const res = await fetch(`${baseUrl}/api/ponds/images/${encodeURIComponent(s3Key)}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "image/*",
      },
    });
    if (res.ok) {
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    }
  };

  const renderImage = (s3Key: string, idx: number) => {
    const src = blobUrls[s3Key] || (s3Key.startsWith("blob:") || s3Key.startsWith("http") ? s3Key : undefined);

    if (!src) {
      return (
        <Box
          key={idx}
          sx={{
            width: 40,
            height: 40,
            backgroundColor: loading[s3Key] ? "grey.200" : "grey.100",
            borderRadius: 1,
            border: "1px solid #e0e0e0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "0.75rem",
            color: "text.secondary",
          }}>
          {loading[s3Key] ? "..." : "?"}
        </Box>
      );
    }

    return (
      <Box
        key={idx}
        component="img"
        src={src}
        alt={`Image ${idx + 1}`}
        sx={{
          width: 40,
          height: 40,
          objectFit: "cover",
          borderRadius: 1,
          border: "1px solid #e0e0e0",
          cursor: "pointer",
          "&:hover": {opacity: 0.8},
          backgroundColor: loading[s3Key] ? "grey.200" : "transparent",
        }}
        onClick={() => handleImageClick(s3Key)}
      />
    );
  };

  return (
    <Box>
      <Box sx={{display: "flex", gap: 0.5, flexWrap: "wrap", maxWidth: 200}}>{imageUrls.map((s3Key, idx) => renderImage(s3Key, idx))}</Box>

      {/* Gallery Dialog for viewing all images */}
      <Dialog open={showGallery} onClose={() => setShowGallery(false)} maxWidth="md" fullWidth>
        <DialogTitle>All Images ({imageUrls.length})</DialogTitle>
        <DialogContent>
          <Box sx={{display: "flex", flexWrap: "wrap", gap: 1, mt: 1}}>
            {imageUrls.map((s3Key, idx) => {
              const src = blobUrls[s3Key] || (s3Key.startsWith("blob:") || s3Key.startsWith("http") ? s3Key : undefined);

              if (!src) {
                return (
                  <Box
                    key={idx}
                    sx={{
                      width: 80,
                      height: 80,
                      backgroundColor: loading[s3Key] ? "grey.200" : "grey.100",
                      borderRadius: 1,
                      border: "1px solid #e0e0e0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.75rem",
                      color: "text.secondary",
                    }}>
                    {loading[s3Key] ? "..." : "?"}
                  </Box>
                );
              }

              return (
                <Box
                  key={idx}
                  component="img"
                  src={src}
                  alt={`Image ${idx + 1}`}
                  sx={{
                    width: 80,
                    height: 80,
                    objectFit: "cover",
                    borderRadius: 1,
                    border: "1px solid #e0e0e0",
                    cursor: "pointer",
                    "&:hover": {opacity: 0.8},
                  }}
                  onClick={() => handleImageClick(s3Key)}
                />
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowGallery(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const FeedingTrayTab: React.FC<FeedingTrayTabProps> = ({pondId, cycleId, isCycleActive = true}) => {
  const {t} = useTranslation();
  const [feedingTrayChecks, setFeedingTrayChecks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCheck, setEditingCheck] = useState<any>(null);
  const [formData, setFormData] = useState({
    checkDate: "",
    feedingTime: "",
    checkingTime: "",
    remark: "",
  });
  const [images, setImages] = useState<string[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [saving, setSaving] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [selectedCheckForAnalysis, setSelectedCheckForAnalysis] = useState<any>(null);

  useEffect(() => {
    fetchFeedingTrayChecks();
  }, [pondId, cycleId]);

  const fetchFeedingTrayChecks = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getFeedingTrayChecks(pondId, cycleId);
      console.log("Fetched feeding tray checks:", data.data);
      setFeedingTrayChecks(data.data || data);
    } catch (err) {
      setError("Failed to load feeding tray checks");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (check?: any) => {
    if (check) {
      setEditingCheck(check);
      setFormData({
        checkDate: check.checkDate ? new Date(check.checkDate).toISOString().split("T")[0] : "",
        feedingTime: check.feedingTime || "",
        checkingTime: check.checkingTime || "",
        remark: check.remark || "",
      });
      setImages(check.imageUrls || []);
    } else {
      setEditingCheck(null);
      setFormData({
        checkDate: new Date().toISOString().split("T")[0],
        feedingTime: "08:00",
        checkingTime: "09:00",
        remark: "",
      });
      setImages([]);
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingCheck(null);
    setImages([]);
    setPendingFiles([]);
    setSaving(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Filter out blob URLs - only save S3 keys to database
      const s3ImageUrls = images.filter((url) => !url.startsWith("blob:"));

      const payload = {
        checkDate: formData.checkDate,
        feedingTime: formData.feedingTime,
        checkingTime: formData.checkingTime,
        remark: formData.remark,
        imageUrls: s3ImageUrls, // Only save S3 keys, not blob URLs
      };

      if (editingCheck) {
        await updateFeedingTrayCheck(editingCheck.id, payload);
      } else {
        // Create the feeding tray check first
        const response = await createFeedingTrayCheck(pondId, payload);

        // Extract the ID from the response
        const newCheckId = response.id;

        // Then upload images if any
        if (pendingFiles.length > 0 && newCheckId) {
          await uploadFeedingTrayImages(newCheckId, pendingFiles);
        }
      }
      handleCloseModal();
      fetchFeedingTrayChecks();
    } catch (err) {
      setError("Failed to save feeding tray check");
    } finally {
      setSaving(false);
    }
  };

  const handleUploadImages = async (files: File[]): Promise<string[]> => {
    if (!editingCheck) {
      // For new checks, store the files and create temporary URLs for preview
      setPendingFiles((prev) => [...prev, ...files]);
      const uploadedUrls: string[] = [];
      for (const file of files) {
        const tempUrl = URL.createObjectURL(file);
        uploadedUrls.push(tempUrl);
      }
      return uploadedUrls;
    }
    return await uploadFeedingTrayImages(editingCheck.id, files);
  };

  const handleRemoveImage = async (imageUrl: string): Promise<void> => {
    if (!editingCheck) {
      // For new checks, remove from both images and pending files
      setImages(images.filter((img) => img !== imageUrl));

      // If it's a blob URL, find and remove the corresponding file from pending files
      if (imageUrl.startsWith("blob:")) {
        setPendingFiles((prev) => {
          const newPendingFiles = [...prev];
          // Find the index of the file that corresponds to this blob URL
          for (let i = 0; i < newPendingFiles.length; i++) {
            const tempUrl = URL.createObjectURL(newPendingFiles[i]);
            if (tempUrl === imageUrl) {
              newPendingFiles.splice(i, 1);
              break;
            }
          }
          return newPendingFiles;
        });
      }
      return;
    }
    await removeFeedingTrayImage(editingCheck.id, imageUrl);
  };

  const handleAnalysisComplete = (result: any) => {
    console.log("Analysis completed:", result);
    // Store the analysis result but don't close the dialog
    // The FeedTrayAnalysis component will display the results
    // setShowAnalysis(false); // Remove this line to keep dialog open
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2}}>
        <Typography variant="h5">{t("Feeding Tray Check Records")}</Typography>
        <Button variant="contained" color="primary" onClick={() => handleOpenModal()} startIcon={<AddIcon />} disabled={!isCycleActive}>
          {t("ADD")}
        </Button>
      </Box>
      {!isCycleActive && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("Viewing closed cycle data. Editing is disabled.")}
        </Alert>
      )}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("Date")}</TableCell>
              <TableCell>{t("Feeding Time")}</TableCell>
              <TableCell>{t("Checking Time")}</TableCell>
              <TableCell>{t("Remark")}</TableCell>
              <TableCell>{t("Images")}</TableCell>
              <TableCell>{t("Actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {feedingTrayChecks.map((check) => (
              <TableRow key={check.id}>
                <TableCell>{new Date(check.checkDate).toLocaleDateString()}</TableCell>
                <TableCell>{check.feedingTime}</TableCell>
                <TableCell>{check.checkingTime}</TableCell>
                <TableCell>{check.remark}</TableCell>
                <TableCell>
                  <ImageThumbnails imageUrls={check.imageUrls || []} pondId={pondId} />
                </TableCell>
                <TableCell>
                  <Box sx={{display: "flex", gap: 1}}>
                    <Button variant="outlined" size="small" onClick={() => handleOpenModal(check)} disabled={!isCycleActive}>
                      {t("Edit")}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setSelectedCheckForAnalysis(check);
                        setShowAnalysis(true);
                      }}
                      startIcon={<AnalyticsIcon />}>
                      {t("Analyze")}
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>{editingCheck ? t("Edit Feeding Tray Check") : t("Add Feeding Tray Check")}</DialogTitle>
        <DialogContent sx={{maxHeight: "80vh", overflowY: "auto"}}>
          <Box display="flex" flexDirection="column" gap={3} mt={1}>
            {/* Basic Info Section */}
            <Box sx={{border: "1px solid #e0e0e0", p: 2, borderRadius: 1}}>
              <Typography variant="h6" sx={{mb: 2, color: "primary.main"}}>
                {t("Basic Information")}
              </Typography>
              <Box display="flex" gap={2} mb={2}>
                <TextField
                  label={t("Check Date")}
                  type="date"
                  value={formData.checkDate}
                  onChange={(e) => setFormData({...formData, checkDate: e.target.value})}
                  InputLabelProps={{shrink: true}}
                  fullWidth
                />
                <TextField
                  label={t("Feeding Time")}
                  type="time"
                  value={formData.feedingTime}
                  onChange={(e) => setFormData({...formData, feedingTime: e.target.value})}
                  InputLabelProps={{shrink: true}}
                  fullWidth
                />
                <TextField
                  label={t("Checking Time")}
                  type="time"
                  value={formData.checkingTime}
                  onChange={(e) => setFormData({...formData, checkingTime: e.target.value})}
                  InputLabelProps={{shrink: true}}
                  fullWidth
                />
              </Box>
              <TextField
                label={t("Remark")}
                value={formData.remark}
                onChange={(e) => setFormData({...formData, remark: e.target.value})}
                fullWidth
                multiline
                minRows={2}
                placeholder={t("Add any additional notes or observations...")}
              />
            </Box>

            {/* Images Section */}
            <Box sx={{border: "1px solid #e0e0e0", p: 2, borderRadius: 1}}>
              <Typography variant="h6" sx={{mb: 2, color: "primary.main"}}>
                {t("Images")} ({images.length}/10)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                {t("Upload images for this feeding tray check. Images will be saved to AWS S3 and linked to this record.")}
              </Typography>
              <ImageUpload
                images={images}
                onImagesChange={setImages}
                onUpload={handleUploadImages}
                onRemove={handleRemoveImage}
                maxImages={10}
                disabled={false}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={saving}>
            {t("Cancel")}
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? t("Saving...") : t("Save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Analysis Dialog */}
      <Dialog open={showAnalysis} onClose={() => setShowAnalysis(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {t("Feed Tray Analysis")}
          {selectedCheckForAnalysis && (
            <Typography variant="body2" color="text.secondary">
              {t("Analyzing check from {{date}}", {date: new Date(selectedCheckForAnalysis.checkDate).toLocaleDateString()})}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{minHeight: "400px"}}>
          <FeedTrayAnalysis onAnalysisComplete={handleAnalysisComplete} initialImage={selectedCheckForAnalysis?.imageUrls?.[0]} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAnalysis(false)} variant="outlined">
            {t("Close Analysis")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeedingTrayTab;
